import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, TextField, Snackbar, Alert, Button, IconButton, InputAdornment, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import PlaceIcon from "@mui/icons-material/Place";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import SpaIcon from "@mui/icons-material/Spa";
import IconNextArrow from "../../../../assets/icons/icon-next-arrow.svg";
import { alpha } from "@mui/material/styles";

const MAX_CHAR = 3000;
const MAX_CHAR_SUBJECT = 140;
const SHOW_CATEGORY_UI = false;
const SEED_TYPE_OPTIONS = [
  { key: "see", label: "See", icon: PlaceIcon },
  { key: "eat", label: "Eat", icon: RestaurantIcon },
  { key: "drink", label: "Drink", icon: LocalBarIcon },
  { key: "do", label: "Do", icon: SportsEsportsIcon },
  { key: "shop", label: "Shop", icon: ShoppingBagIcon },
  { key: "relax", label: "Relax", icon: SpaIcon },
];

function normalizeSeedType(value) {
  const next = String(value || "").trim().toLowerCase();
  return SEED_TYPE_OPTIONS.some((option) => option.key === next) ? next : "";
}

export default function Step1ComposeFrictionless({
  wizardData,
  setWizardData,
  onNext,
  onSaveAndExit,
  isSavingDraft = false,
}) {
  const [subjectError, setSubjectError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const initialSubjectRef = useRef(String(wizardData?.subject || ""));
  const initialMessageRef = useRef(String(wizardData?.message || ""));

  const navigate = useNavigate();

  const actionBarPaddingBottom = useMemo(
    () => "calc(env(safe-area-inset-bottom) + 12px)",
    []
  );
  const cancelState = useMemo(() => {
    if (!Number.isFinite(wizardData?.lat) || !Number.isFinite(wizardData?.lng)) return undefined;
    return { center: { lat: wizardData.lat, lng: wizardData.lng } };
  }, [wizardData?.lat, wizardData?.lng]);

  const showSnack = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const handleSaveAndExit = () => {
    if (typeof onSaveAndExit === "function") {
      onSaveAndExit();
      return;
    }
    navigate("/", { state: cancelState });
  };

  const handleSubjectChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length > MAX_CHAR_SUBJECT) {
      setSubjectError(`Subject cannot exceed ${MAX_CHAR_SUBJECT} characters.`);
    } else {
      setSubjectError("");
      setWizardData((prev) => ({ ...prev, subject: newValue }));
    }
  };

  const handleMessageChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length > MAX_CHAR) {
      setMessageError(`Message cannot exceed ${MAX_CHAR} characters.`);
    } else {
      setMessageError("");
      setWizardData((prev) => ({ ...prev, message: newValue }));
    }
  };

  const handleToggleSeedType = (key) => {
    const nextKey = normalizeSeedType(key);
    if (!nextKey) return;
    setWizardData((prev) => {
      const current = normalizeSeedType(prev?.seedType);
      return {
        ...prev,
        seedType: current === nextKey ? "" : nextKey,
      };
    });
  };

  const handleNextStep = () => {
    if (!wizardData.message?.trim()) {
      showSnack("Message is required.");
      return;
    }
    onNext();
  };

  const handleClearSubject = () => {
    setSubjectError("");
    setWizardData((prev) => ({ ...prev, subject: "" }));
  };

  const handleRestoreSubject = () => {
    setSubjectError("");
    setWizardData((prev) => ({ ...prev, subject: initialSubjectRef.current }));
  };

  const handleClearMessage = () => {
    setMessageError("");
    setWizardData((prev) => ({ ...prev, message: "" }));
  };

  const handleRestoreMessage = () => {
    setMessageError("");
    setWizardData((prev) => ({ ...prev, message: initialMessageRef.current }));
  };

  const showRestoreSubject = !String(wizardData.subject || "") && Boolean(initialSubjectRef.current);
  const showRestoreMessage = !String(wizardData.message || "") && Boolean(initialMessageRef.current);
  const selectedSeedType = normalizeSeedType(wizardData?.seedType);

  return (
    <Box sx={{ flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowY: "scroll",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorY: "contain",
          touchAction: "pan-y",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          px: 4,
          pt: 2,
          pb: 2,
        }}
      >
        <Box sx={{ mt: 1.25, mb: 1.5 }}>
          <TextField
            label="Subject*"
            fullWidth
            value={wizardData.subject || ""}
            onChange={handleSubjectChange}
            error={Boolean(subjectError)}
            helperText={subjectError || `${(wizardData.subject || "").length}/${MAX_CHAR_SUBJECT}`}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={showRestoreSubject ? handleRestoreSubject : handleClearSubject}
                      disabled={!showRestoreSubject && !String(wizardData.subject || "")}
                      aria-label={showRestoreSubject ? "Restore subject" : "Clear subject"}
                      edge="end"
                      sx={(theme) => ({
                        width: 28,
                        height: 28,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.secondary,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      })}
                    >
                      {showRestoreSubject ? <UndoRoundedIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Box sx={{ position: "relative", mt: 1, mb: 1 }}>
          <TextField
            label="Your Message*"
            multiline
            minRows={5}
            fullWidth
            value={wizardData.message || ""}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={handleMessageChange}
            error={Boolean(messageError)}
            helperText={messageError || `${(wizardData.message || "").length}/${MAX_CHAR}`}
            sx={{
              "& .MuiOutlinedInput-input": {
                pr: 5,
                pb: 5,
              },
            }}
          />
          <IconButton
            size="small"
            onClick={showRestoreMessage ? handleRestoreMessage : handleClearMessage}
            disabled={!showRestoreMessage && !String(wizardData.message || "")}
            aria-label={showRestoreMessage ? "Restore message" : "Clear message"}
            sx={(theme) => ({
              position: "absolute",
              bottom: 34,
              right: 10,
              width: 28,
              height: 28,
              border: "1px solid",
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            })}
          >
            {showRestoreMessage ? <UndoRoundedIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
          </IconButton>
        </Box>

        {SHOW_CATEGORY_UI && (
          <Box
            sx={(theme) => ({
              mt: 2,
              mb: 1,
              px: 1.5,
              py: 1.5,
              borderRadius: 1,
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? theme.palette.divider
                  : alpha(theme.palette.primary.main, 0.2)
              }`,
            })}
          >
            <Box sx={{ mb: 1.2 }}>
              <Box sx={{ fontSize: "0.95rem", fontWeight: 700 }}>Capsule Category (Optional)</Box>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1,
              }}
            >
              {SEED_TYPE_OPTIONS.map((option) => {
                const selected = selectedSeedType === option.key;
                const IconComponent = option.icon;
                return (
                  <Button
                    key={option.key}
                    onClick={() => handleToggleSeedType(option.key)}
                    variant="outlined"
                    startIcon={<IconComponent fontSize="small" />}
                    sx={(theme) => ({
                      textTransform: "none",
                      justifyContent: "flex-start",
                      borderRadius: 999,
                      py: 1.1,
                      borderColor: selected ? theme.palette.success.main : theme.palette.divider,
                      color: selected ? theme.palette.success.main : theme.palette.text.primary,
                      backgroundColor: selected
                        ? theme.palette.mode === "dark"
                          ? alpha(theme.palette.success.main, 0.15)
                          : alpha(theme.palette.success.main, 0.08)
                        : "transparent",
                      "&:hover": {
                        borderColor: selected ? theme.palette.success.main : theme.palette.primary.main,
                        backgroundColor: selected
                          ? theme.palette.mode === "dark"
                            ? alpha(theme.palette.success.main, 0.2)
                            : alpha(theme.palette.success.main, 0.12)
                          : theme.palette.action.hover,
                      },
                    })}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Box>
            <Box
              sx={{
                mt: 1,
                minHeight: 20,
                fontSize: "0.8rem",
                color: "text.secondary",
              }}
            >
              {selectedSeedType
                ? `Selected: ${SEED_TYPE_OPTIONS.find((option) => option.key === selectedSeedType)?.label || selectedSeedType}`
                : "No category selected"}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: "0 0 auto",
          mt: "auto",
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderTopColor: "divider",
          px: 2,
          pt: 1.5,
          pb: actionBarPaddingBottom,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", width: "100%" }}>
          <IconButton
            onClick={handleSaveAndExit}
            sx={(theme) => ({
              borderRadius: "999px",
              width: 48,
              height: 48,
              flex: "0 0 auto",
              border: "1px solid",
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.mode === "dark"
                ? alpha(theme.palette.text.primary, 0.02)
                : "transparent",
              color: theme.palette.text.primary,
              "&:hover": {
                boxShadow: "none",
                backgroundColor: theme.palette.mode === "dark"
                  ? alpha(theme.palette.text.primary, 0.06)
                  : alpha(theme.palette.text.primary, 0.02),
              },
            })}
            aria-label="Back and save"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Button
            variant="outlined"
            onClick={handleSaveAndExit}
            disabled={isSavingDraft}
            startIcon={isSavingDraft ? <CircularProgress size={16} color="inherit" /> : null}
            sx={(theme) => ({
              fontWeight: 700,
              px: 3,
              py: 1.25,
              flex: 1,
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.mode === "dark"
                ? alpha(theme.palette.text.primary, 0.02)
                : "transparent",
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: theme.palette.mode === "dark"
                  ? alpha(theme.palette.text.primary, 0.06)
                  : alpha(theme.palette.text.primary, 0.02),
              },
              "&.Mui-disabled": {
                color: theme.palette.text.disabled,
                borderColor: theme.palette.action.disabledBackground,
                backgroundColor: "transparent",
              },
            })}
          >
            {isSavingDraft ? "Saving..." : "Save & exit"}
          </Button>

          <IconButton
            onClick={handleNextStep}
            sx={(theme) => ({
              borderRadius: "999px",
              width: 48,
              height: 48,
              flex: "0 0 auto",
              backgroundColor: theme.colors.blue,
              color: theme.palette.common.white,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            })}
            aria-label="Next step"
          >
            <Box component="img" src={IconNextArrow} alt="" sx={{ width: 16, height: 16 }} />
          </IconButton>
        </Box>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} variant="filled" severity="error" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
